import {AfterViewInit, ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, ViewChild} from "@angular/core";
import {CommonModule} from "@angular/common";
import {NgScrollbar, NgScrollbarModule} from "ngx-scrollbar";
import {TablerIconsModule} from "angular-tabler-icons";
import {FormsModule, ReactiveFormsModule} from "@angular/forms";
import {MaterialModule} from "../../../material.module";
import {MatSidenavModule} from "@angular/material/sidenav";
import {ChatMessageDTO, ChatSessionDTO} from "./ChatSessionDTO";
import {LlmModelDTO} from "../llm-settings/models/llm-model.dto";
import {LlmSettingsDTO} from "../llm-settings/models/llm-settings.dto";
import {OrganisationDTO} from "../../../DTO/Organisation/OrganisationDTO";
import {SafeHtml} from "@angular/platform-browser";
import {ChatService, StreamResponse} from "../../../services/apps/chat/chat.service";
import {MarkdownService} from "../../../services/markdown.service";
import {LlmSettingsService} from "../llm-settings/services/llm-settings.service";
import {ToastrService} from "ngx-toastr";
import {ActivatedRoute, Router} from "@angular/router";
import {AuthorizationService} from "../../../../core/authorization/AuthorizationService";
import {AuthInfoDTO} from "../../../../core/dto/AuthInfoDTO";
import {OrganisationAccountDTO} from "../../../../core/dto/OrganisationAccountDTO";
import {TRUE} from "sass";
import {MediaViewerService} from "../../../services/media-viewer.service";
import {FileUploadService} from "../../../services/apps/file/file-upload.service";
import {firstValueFrom, take} from "rxjs";
import { BrandingComponent } from "src/app/layouts/full/vertical/sidebar/branding.component";
import { RouterModule } from "@angular/router";
import { ThemeService } from "src/app/services/theme.service";
import { AppSettings } from "src/app/config";
import { LocalizationService } from "src/app/services/localizationService";
import { LocalizationPipe } from "src/app/pipe/localization-pipe";
import {
  DeleteConfirmationData,
  DeleteConfirmationDialogComponent
} from "../file/resource-management/delete-confirmation-dialog.component";
import {MatDialog} from "@angular/material/dialog";

@Component({
  selector: 'app-chat',
  standalone: true,
  imports: [
    CommonModule,
    NgScrollbarModule,
    TablerIconsModule,
    FormsModule,
    ReactiveFormsModule,
    MaterialModule,
    MatSidenavModule,
    BrandingComponent,
    RouterModule,
    LocalizationPipe,
  ],
  templateUrl: './chat.component.html',
})
export class AppChatComponent implements OnInit, AfterViewInit, OnDestroy {
  options: AppSettings | null = null;
  sidePanelOpened = true;
  messageText = '';
  isSearching: boolean = false;

  selectedSession: ChatSessionDTO | null = null;
  userSessions: ChatSessionDTO[] = [];
  filteredSessions: ChatSessionDTO[] = [];
  searchTerm = '';
  isStreaming = false;
  availableModels: LlmModelDTO[] = [];
  llmSettings: LlmSettingsDTO = new LlmSettingsDTO();
  organization: OrganisationDTO | OrganisationAccountDTO = null;
  selectedModel: string = '';
  botImage: string = 'assets/images/logos/bilgebot-cropped-dark.png';
  botImageLight: string = 'assets/images/logos/bilgebot-cropped.png';
  activeUser: AuthInfoDTO | null = null
  imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'];
  videoExtensions = ['mp4', 'avi', 'mov', 'wmv', 'flv', 'mkv', 'webm', '3gp', 'mpeg', 'mpg', 'm4v'];
  public languages: any[] = [];

  private renderedCache = new Map<string, SafeHtml>();

  @ViewChild('chatScrollbar') scrollbar!: NgScrollbar;
  @ViewChild('messageInput') messageInput!: ElementRef<HTMLInputElement>;

  constructor(
    private chatService: ChatService,
    private cdr: ChangeDetectorRef,
    public markdownService: MarkdownService,
    private llmSettingsService: LlmSettingsService,
    private toastrService: ToastrService,
    private route: ActivatedRoute,
    private router: Router,
    private authorizationService: AuthorizationService,
    private mediaViewerService: MediaViewerService,
    private fileService: FileUploadService,
    private themeService: ThemeService,
    private localizationService: LocalizationService,
    private dialog: MatDialog,
    private toastService: ToastrService,
  ) {}

  ngOnInit(): void {
    this.options = this.themeService.getOptions();
    this.activeUser = this.authorizationService.getActiveUser();
    this.organization = this.activeUser.organisationAccount;

    // Initialize languages from centralized service
    this.languages = this.localizationService.availableLanguages;

    if(this.organization && this.organization.id) {
      this.getCurrentUserLlmSettings();

      this.getCurrentUserChatSessions().then(() => {
        // Check if there's a session ID in the URL
        this.route.params.subscribe(params => {
          const sessionId = params['sessionId'];
          if (sessionId) {
            this.openSessionFromUrl(sessionId);
          }
        });
      });

      if (this.isOver()) {
        this.sidePanelOpened = false;
      }
    }

  }
  changeLanguage(lang: any): void {
    this.localizationService.setCurrentLang(lang.code);
  }

  setLightDark(theme: string) {
    if (theme === 'light' || theme === 'dark') {
      this.themeService.setTheme(theme as 'light' | 'dark');
      this.options = this.themeService.getOptions();
    }
  }

  ngAfterViewInit() {
    this.scrollChatToBottom();
    this.focusMessageInput();
  }

  ngOnDestroy(): void {
    this.renderedCache.clear();
  }

  getRenderedContent(msg: string): SafeHtml {
    if (!this.renderedCache.has(msg)) {
      this.renderedCache.set(msg, this.markdownService.render(msg));
    }
    return this.renderedCache.get(msg)!;
  }

  isOver(): boolean {
    return window.matchMedia(`(max-width: 960px)`).matches;
  }

  handleModelClick(event: MouseEvent): void {
    if (!this.availableModels || this.availableModels.length === 0) {
      event.stopPropagation(); // prevent the panel from opening
      this.toastrService.warning(
        'Bu kurum için henüz LLM ayarları oluşturulmamış. Lütfen önce LLM ayarlarını oluşturun.', // TODO : Localize
        'Uyarı',
        {
          progressBar: true,
          timeOut: 5000,
          closeButton: true,
          tapToDismiss: true
        }
      );
    }
  }

  getCurrentUserChatSessions(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.chatService.getUserChatSessions().subscribe({
        next: (sessions) => {
          this.userSessions = [];
          this.filteredSessions = [];
          for (let session of sessions.sessions) {
            const sessionDTO = new ChatSessionDTO();
            sessionDTO.bindData(session);
            this.userSessions.push(sessionDTO);
            this.filteredSessions.push(sessionDTO);
          }
          resolve();
        },
        error: (error) => {
          console.error('Error in chat sessions:', error);
          reject(error);
        }
      });
    });
  }

  getCurrentUserLlmSettings(){
    this.llmSettingsService.getOrganizationLlmSettings(this.organization)
      .subscribe({
        next: (response: any) => {
          this.llmSettings.bindData(response.data);
          if (this.llmSettings.id == null|| this.llmSettings.id == 0) {
            this.toastrService.error("",
              'Bu kurum için henüz LLM ayarları oluşturulmamış. Lütfen önce LLM ayarlarını oluşturun.', // TODO : Localize
              { disableTimeOut: true, closeButton: true, tapToDismiss: false }
            );
          }
          this.availableModels = this.llmSettings.availableModels;
          this.selectedModel = this.availableModels.find(model => model.isDefault)?.id || '';
        },
        error: (error) => {
          console.error('Ayarlar yüklenirken bir hata oluştu:', error);
        }
      });
    if (!this.llmSettings) {
      this.toastrService.error('Bu kurum için henüz LLM ayarları oluşturulmamış. Lütfen önce LLM ayarlarını yapın.');  // TODO : Localize
    }
  }

  changeModel(modelId: string): void {
    this.selectedModel = modelId;
    const selectedModelObj = this.availableModels.find(model => model.id === modelId);
    if (selectedModelObj) {

      // If there's an active conversation, inform the user about the model change
      if (this.selectedSession && this.selectedSession.messages && this.selectedSession.messages.length > 0) {
        const systemMessage = `Model changed to ${selectedModelObj.displayName}. Messages from this point will use the new model.`; // TODO : Localize
        this.addBotResponse(systemMessage);
        this.scrollChatToBottom();
      }
    }
  }

  scrollChatToBottom() {
    setTimeout(() => {
      this.scrollbar?.scrollTo({ bottom: 0, duration: 200 });
    }, 100);
  }

  focusMessageInput() {
    setTimeout(() => {
      if (this.messageInput?.nativeElement && !this.isStreaming) {
        this.messageInput.nativeElement.focus();
        // Force Angular to check for changes
        this.cdr.detectChanges();
      }
    }, 100);
  }
  newChat(){
    this.router.navigate(['/chat'], { replaceUrl: true });
  }
  selectSession(session: ChatSessionDTO): void {
    this.selectedSession = session;

    // Update URL to reflect the selected session
    if (session.id) {
      this.router.navigate(['/chat', session.id], { replaceUrl: true });
      this.loadSessionMessages(session.id);
    }

    if (this.isOver()) {
      // Check if the screen is small
      this.sidePanelOpened = false; // Close the sidebar
    }

    // Focus the input when a session is selected
    this.focusMessageInput();
  }

  deleteSession(session: ChatSessionDTO): void {
    const dialogRef = this.dialog.open(DeleteConfirmationDialogComponent, {
      width: '400px',
      data: { fileName: session.session_title,modalTitle:this.localizationService.getLocalizationValue("DeleteConfirmModalTitle"),modalQuestion:this.localizationService.getLocalizationValue("DeleteConfirmModalMessage") } as DeleteConfirmationData,
      disableClose: true // Dialog'un dışına tıklayarak kapatılmasını engelle
    });

    // Dialog component'ine referans al
    const dialogComponent = dialogRef.componentInstance;

    // Dialog'dan gelen deleteConfirmed event'ini dinle
    dialogComponent.deleteConfirmed.subscribe(() => {

      this.chatService.deleteChatSession(session.id).pipe(
          take(1) // observable'ı tamamlar
      ).subscribe({
        next: () => {
          this.toastService.success( this.localizationService.getLocalizationValue('DeleteSuccess'));
          dialogRef.close();
          this.router.navigate(['/chat']);
        },
        error: (err) => {
          this.toastrService.error(this.localizationService.getLocalizationValue('DeleteFailed'), 'Error');
          dialogRef.close();
        }
      });
    });

  }


  openSessionFromUrl(sessionId: string): void {
    const session = this.userSessions.find(s => s.id === sessionId);
    if (session) {
      this.selectedSession = session;
      this.loadSessionMessages(sessionId);
      if (this.isOver()) {
        this.sidePanelOpened = false;
      }
      this.focusMessageInput();
    } else {
      this.loadSessionDirectly(sessionId);
    }
  }

  loadSessionDirectly(sessionId: string): void {
    const tempSession = new ChatSessionDTO();
    tempSession.id = sessionId;
    tempSession.session_title = 'Loading...';

    this.selectedSession = tempSession;

    this.loadSessionMessages(sessionId);
  }

  clearSession(): void {
    this.selectedSession = null;
    this.router.navigate(['/chat'], { replaceUrl: true });
  }

  loadSessionMessages(sessionId: string): void {
    this.chatService.getSessionMessages(sessionId).subscribe({
      next: async (messages) => {
        if (this.selectedSession && this.selectedSession.id === sessionId) {
          // Process messages to reconstruct media content
          const processedMessages = await Promise.all(messages.map(async (message) => {
            if (message.role === 'assistant' && message.metadata && message.metadata.document_links) {
              // Reconstruct media content for assistant messages with document links
              const mediaContent = await this.reconstructMediaContent(message.content, message.metadata.document_links);
              // Create a new ChatMessageDTO with updated content
              const updatedMessage = new ChatMessageDTO();
              updatedMessage.bindData({
                id: message.id,
                session_id: message.sessionId,
                role: message.role,
                content: mediaContent,
                metadata: message.metadata,
                created_at: message.createdAt
              });
              return updatedMessage;
            }
            return message;
          }));

          // Update the selected session with the processed messages
          this.selectedSession.messages = processedMessages;

          // Scroll to bottom after messages are loaded
          setTimeout(() => {
            this.scrollChatToBottom();
          }, 100);
        }
      },
      error: (error) => {
        if(error.status && error.status===404){
          this.router.navigate(['/chat']);
        }
        console.error('Error loading session messages:', error);
        this.toastrService.error('Failed to load session messages', 'Error');
      }
    });
  }

  async reconstructMediaContent(originalContent: string, documentLinks: any[]): Promise<string> {
    if (!documentLinks || documentLinks.length === 0) {
      return originalContent;
    }

    let mediaContent = `\n\n**Referans Dokümanlar:**\n\n`;

    // Process all document links sequentially to maintain order
    for (let index = 0; index < documentLinks.length; index++) {
      const link = documentLinks[index];
      try {
        if (link.file_key !== null) {
          const response = await firstValueFrom(this.fileService.viewFile(this.organization.id, link.url));
          const blob = response.body;
          const objectUrl = URL.createObjectURL(blob);
          const title = link.title || link.file_name || `Document ${index + 1}`;
          const fileExtension = link.file_key ? link.file_key.split('?')[0].split('.').pop()?.toLowerCase() : '';
          const docType = link.doc_type || null;

          if (docType === 'image') {
            // Display image directly with title
            mediaContent += `\n**${title}**\n`;
            mediaContent += `<img src="${objectUrl}" alt="${title}" style="max-width: 25%; height: auto; border-radius: 8px; margin: 8px 0;" />\n\n`;
          } else if (docType === 'video') {
            mediaContent += `\n**${title}**\n`;
            mediaContent += `<video controls style="max-width: 50%; height: auto; border-radius: 8px; margin: 8px 0;">\n`;
            mediaContent += `  <source src="${objectUrl}" type="video/${fileExtension}">\n`;
            mediaContent += `  Your browser does not support the video tag.\n`;
            mediaContent += `</video>\n\n`;
          } else {
            mediaContent += `\n**${title}**\n`;
            mediaContent += `📄 [${title}](/media-viewer?fileUrl=${objectUrl}&fileName=${encodeURIComponent(title)})\n\n`;
          }
        } else {
          mediaContent += `\n**${link.title}**\n`;
          mediaContent += `🌐 [${link.title}](${link.url})\n\n`;
        }
      } catch (error) {
        console.error('Error loading file:', error);
        const title = link.title || link.file_name || `Document ${index + 1}`;
        mediaContent += `\n**${title}**\n`;
        mediaContent += `⚠️ *Dosya yüklenirken hata oluştu*\n\n`; // TODO : Localize
      }
    }

    return originalContent + mediaContent;
  }

  addUserMessage(content: string): void {
    if (!this.selectedSession) {
      this.selectedSession = new ChatSessionDTO();
      this.selectedSession.id = '';
      this.selectedSession.messages = [];
    }

    const msg = new ChatMessageDTO();
    msg.sessionId = this.selectedSession.id;
    msg.role = 'user';
    msg.content = content;
    msg.createdAt = new Date();

    this.selectedSession.messages.push(msg);
  }

  addBotResponse(content: string): void {
    if (!this.selectedSession) return;

    const msg = new ChatMessageDTO();
    msg.sessionId = this.selectedSession.id;
    msg.role = 'assistant';
    msg.content = content;
    msg.createdAt = new Date();

    if (!this.selectedSession.messages) {
      this.selectedSession.messages = [];
    }

    this.selectedSession.messages.push(msg);
  }

  updateLastBotMessage(content: string): void {
    if (!this.selectedSession || !this.selectedSession.messages || this.selectedSession.messages.length === 0) {
      return;
    }

    const messages = this.selectedSession.messages;
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].role === 'assistant') {
        messages[i].content = content;
        break;
      }
    }
  }

  openFilesWithMediaComponent(doc) {

    if (!doc) {
      this.toastrService.error('Doküman bilgileri eksik.', 'Hata');
      return;
    }

    const fileKey = doc.file_key;
    const fileName = doc.metadata.file_name || 'Bilinmeyen Dosya';

    if (!fileKey) {
      this.toastrService.error('Dosya anahtarı bulunamadı.', 'Hata');
      return;
    }

    if (!this.organization || !this.organization.id) {
      this.toastrService.error('Organizasyon bilgisi bulunamadı.', 'Hata');
      return;
    }

    // Önce token al, sonra media viewer'ı aç
    this.fileService.getFileToken(this.organization.id, fileKey).subscribe({
      next: (tokenResponse: any) => {
        // Token ile media viewer'ı aç - viewFile response'ı içeride kullanılacak
        this.mediaViewerService.openFile(this.organization.id, tokenResponse, fileName);
      },
      error: err => {
        console.error('Token alma hatası:', err);
        console.error('Token error details:', JSON.stringify(err, null, 2));
        this.toastrService.error('Dosya erişim token\'ı alınamadı.');
      }
    });
    this.mediaViewerService.openFile(
      this.organization.id,
      fileKey,
      fileName
    );
  }

  async sendMessage(): Promise<void> {
    if (!this.messageText.trim()) {
      return;
    }
    // Check if model is selected
    if (!this.selectedModel) {
      this.toastrService.warning('Please select an AI model before sending a message.'); // TODO : Localize
      return;
    }

    const userMessage = this.messageText.trim();
    // Reset streaming state
    this.isStreaming = true;
    // Add user message to the UI
    this.addUserMessage(userMessage);

    // Clear input field
    this.messageText = '';
    // Scroll to bottom
    this.scrollChatToBottom();

    // Add a placeholder for the bot's response with loading indicator
    this.addBotResponse('Araştırıyorum, lütfen bekleyiniz...');

    // Get the selected model from the dropdown
    const modelObj = this.availableModels.find(m => m.id === this.selectedModel);

    // Track accumulated text for building the complete response
    let accumulatedText = '';

    // Call the API to get a streaming response with the selected model
    this.chatService.streamChatMessage(this.selectedSession?.id ?? '', userMessage, modelObj.modelName).subscribe({
      next: (response: StreamResponse) => {
        this.isSearching = true;
        switch (response.type) {
          case 'search_results':
            this.selectedSession.messages[this.selectedSession.messages.length - 1].searchResults = [];
            if (response.documents && response.documents.length > 0) {
              response.documents.forEach((doc: any, index: number) => {
                this.selectedSession.messages[this.selectedSession.messages.length - 1].searchResults.push(doc);
              });
              this.scrollChatToBottom();
            } else {
              let noResultsContent = `\n\n**🔍 Arama Sonuçları:**\n\n`;
              noResultsContent += `*Aradığınız konuyla ilgili doküman bulunamadı.*\n\n`;
              noResultsContent += `*Lütfen farklı anahtar kelimeler deneyebilir veya sorunuzu yeniden formüle edebilirsiniz.*\n\n`;

              accumulatedText += noResultsContent;
              this.updateLastBotMessage(accumulatedText);
              // Cache'i temizle ve yeniden render et
              this.renderedCache.delete(accumulatedText);
              this.scrollChatToBottom();
            }
            break;
          case 'text':
            // Accumulate text chunks
            if (response.text) {
              accumulatedText += response.text;
              this.updateLastBotMessage(accumulatedText);
              // Clear the cache entry for this message to force re-rendering
              this.renderedCache.delete(accumulatedText);
              this.scrollChatToBottom();
            }
            break;

          case 'text_correction':
            // Handle text correction - replace the accumulated text with the corrected version
            if (response.textCorrection !== undefined) {
              this.selectedSession.messages[this.selectedSession.messages.length - 1].finalContentDocument = []
              accumulatedText = response.textCorrection;
              this.updateLastBotMessage(accumulatedText);
              // Clear cache and re-render
              this.renderedCache.delete(accumulatedText);
              this.scrollChatToBottom();
            }
            break;

          case 'error':
            // Handle errors
            console.error('Stream error:', response.error);
            this.updateLastBotMessage(`Error: ${response.error}`);
            this.isStreaming = false;
            this.focusMessageInput();
            break;

          case 'done':
            // Stream completed successfully
            if (response.sessionId) {
              // Update the current session ID if it was created
              if (this.selectedSession && !this.selectedSession.id) {
                this.selectedSession.id = response.sessionId;
                // Update URL to reflect the new session
                this.router.navigate(['/chat', response.sessionId], { replaceUrl: true });
              }
            }
            if (response.documentMetadata) {
              // Doküman metadatasına göre işlem yapılacak
              let mediaContent = `\n\n**${response.documentMetadata.file_name}:**\n\n`;

              const processMediaLinks = async () => {
                for (let index = 0; index < response.documentMetadata.length; index++) {
                  const metadata = response.documentMetadata[index];
                  const title = metadata.title || metadata.name || `Document ${index + 1}`;
                  const docType = metadata.doc_type || null;

                  // Videoların mime type değeri için file extension alınıyor.
                  const fileExtension = metadata.file_key ? metadata.file_key.split('?')[0].split('.').pop()?.toLowerCase() : '';

                  // Önce "yükleniyor" mesajını ekle
                  let tempMediaContent = `\n**${title}**\n`;
                  tempMediaContent += `<span class="loading-pulse">⏳ *Dosyanız yükleniyor...*</span>\n\n`; // TODO : Localize

                  // Geçici içeriği güncelle
                  let currentAccumulatedText = accumulatedText + mediaContent + tempMediaContent;
                  this.updateLastBotMessage(currentAccumulatedText);
                  this.renderedCache.delete(currentAccumulatedText);
                  this.scrollChatToBottom();

                  // Dosya url default olarak file-service üzerinden geliyor.
                  let fileContent = '';

                  if (metadata.file_key === null) {
                    fileContent += `\n**${title}**\n`;
                    fileContent += `🌐 [${title}](${metadata.url})\n\n`;
                  } else {
                    try {

                      const fileResponse = await firstValueFrom(this.fileService.viewFile(this.organization.id, metadata.url));
                      const blob = fileResponse.body;
                      const objectUrl = URL.createObjectURL(blob);

                      if (fileResponse.body && fileResponse.body.size > 0) {
                        if (docType === 'image') {
                          fileContent += `\n**${title}**\n`;
                          fileContent += `<img src="${objectUrl}" alt="${title}" style="max-width: 25%; height: auto; border-radius: 8px; margin: 8px 0;" />\n\n`;
                        } else if (docType === 'video') {

                          // Display video player with title
                          fileContent += `\n**${title}**\n`;
                          fileContent += `<video controls style="max-width: 50%; height: auto; border-radius: 8px; margin: 8px 0;">\n`;
                          fileContent += `  <source src="${objectUrl}" type="video/${fileExtension}">\n`;
                          fileContent += `  Your browser does not support the video tag.\n`;
                          fileContent += `</video>\n\n`;
                        } else {
                          // For non-image/video files (PDF, DOC, etc.), provide a view link
                          fileContent += `\n**${title}**\n`;
                          fileContent += `📄 [${title}](/media-viewer?fileUrl=${objectUrl}&fileName=${encodeURIComponent(title)})\n\n`;
                        }
                      } else {
                        console.warn('Empty or invalid blob received for image:', metadata.url);
                        fileContent += `\n**${title}**\n`;
                        fileContent += `⚠️ *Medya yüklenemedi*\n\n`; // TODO : Localize
                      }
                    } catch (error) {
                      console.error('Error loading image:', error);
                      fileContent += `\n**${title}**\n`;
                      fileContent += `⚠️ *Medya yüklenirken hata oluştu*\n\n`; // TODO : Localize
                    }
                  }
                  // Her dosya işlendikten sonra güncel içeriği göster
                  mediaContent += fileContent;
                  accumulatedText += fileContent;
                  this.updateLastBotMessage(accumulatedText);
                  this.renderedCache.delete(accumulatedText);
                  this.scrollChatToBottom();
                }
              };

              processMediaLinks();
            }
            this.isSearching = false
            this.selectedSession.messages[this.selectedSession.messages.length - 1].searchResults = [];

            // TODO : Eğer html işlemleri inline olarak yapılamıyor ise direkt olarak angular içerisinde setlemek için kullanılıyor.
            // if (response.documentMetadata) {
            //   response.documentMetadata.forEach((documentMetadata) => {
            //     this.selectedSession.messages[this.selectedSession.messages.length - 1].finalContentDocument.push(documentMetadata);
            //   })
            // }
            break;
        }
      },
      error: (error) => {
        console.error('Error in chat stream:', error);
        this.isStreaming = false;

        // Update the bot message to show the error
        this.updateLastBotMessage('Sorry, an error occurred while processing your request.');

        // Re-focus the input field
        this.focusMessageInput();
      },
      complete: () => {
        // Streaming is complete
        this.isStreaming = false;
        this.scrollChatToBottom();
        // Re-focus the input field
        this.focusMessageInput();
      }
    });
  }

  searchSessions(): void {
    if (this.searchTerm.trim()) {
      this.filteredSessions = this.userSessions.filter((session) =>
        session.session_title.toLowerCase().includes(this.searchTerm.toLowerCase())
      );
    } else {
      this.filteredSessions = this.userSessions;
    }
  }

  protected readonly TRUE = TRUE;
}
